import React, { ReactElement } from "react";
import moment from "moment";
import { convertPascalCaseToWords, Lead, LeadQuality } from "../../../../core";
import { ListTableColumn } from "../../../../core/components/list-table";
import { CrmAgentColumn, EmptyPersonColumn, SalesPersonColumn } from "../../../../components/list-columns";

type LeadColumnKey =
  | "title"
  | "date"
  | "companyName"
  | "contactName"
  | "mobile"
  | "leadIntent"
  | "leadSource"
  | "qualityReason"
  | "transactionManager"
  | "leadManager"
  | "groupManager"
  | "telecaller"
  | "supportExecutive"
  | "email"
  | "createdBy"
  | "cityHead"
  | "supportExecutiveAssignedDate"
  | "transactionManagerAssignedDate"
  | "cityHeadAssignedDate"
  | "leadManagerAssignedDate"
  | "groupManagerAssignedDate"
  | "telecallerAssignedDate"
  | "quality"
  | "transactionManagerAssignedBy"
  | "supportExecutiveAssignedBy"
  | "leadManagerAssignedBy"
  | "groupManagerAssignedBy"
  | "telecallerAssignedBy";

const leadColumns: Record<LeadColumnKey, ReactElement> = {
  title: (
    <ListTableColumn
      label="Title"
      source="title"
      column="title"
      settingsGroup="Lead"
    />
  ),
  date: (
    <ListTableColumn
      label="Date"
      source="createdAt"
      column="createdAt"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY")
      }
      sortable
      showDefault
      settingsGroup="Lead"
    />
  ),
  companyName: (
    <ListTableColumn
      label="Company Name"
      source="company.name"
      column="companyName"
      showDefault
      settingsGroup="Person"
      sortKey="customer.name"
    />
  ),
  contactName: (
    <ListTableColumn
      label="Contact Name"
      source="customer.fullName"
      column="name"
      showDefault
      settingsGroup="Person"
      sortKey="customer.fullName"
    />
  ),
  mobile: (
    <ListTableColumn
      label="Mobile"
      source="customer.phone"
      column="phone"
      settingsGroup="Person"
      sortKey="customer.phone"
      showDefault
    />
  ),
  leadIntent: (
    <ListTableColumn
      label="Lead Intent"
      source="leadIntent.name"
      column="leadIntent"
      showDefault
      settingsGroup="Lead"
    />
  ),
  leadSource: (
    <ListTableColumn
      label="Lead Source"
      source="leadSource.name"
      column="leadSource"
      settingsGroup="Lead"
      sortKey="leadSource.name"
      sortable
      showDefault
    />
  ),
  qualityReason: (
    <ListTableColumn
      label="Quality Reason"
      column="lslReason"
      settingsGroup="Lead"
      formatContent={(d: Lead) =>
        d.quality !== LeadQuality.Interested
          ? convertPascalCaseToWords(d.qualityInfo?.reason)
          : ""
      }
      excelSource="qualityInfo.reason"
      showDefault
    />
  ),
  transactionManager: (
    <ListTableColumn
      label="Transaction Manager"
      source="currentSalesPerson"
      excelSource="currentSalesPerson.name"
      column="currentSalesPerson"
      settingsGroup="Person"
      formatContent={(user) => <SalesPersonColumn user={user} />}
      showDefault
      nullContent={<EmptyPersonColumn />}
    />
  ),
  leadManager: (
    <ListTableColumn
      label="Lead Manager"
      source="leadManager"
      excelSource="leadManager.name"
      column="leadManager"
      settingsGroup="Person"
      formatContent={(user) => <SalesPersonColumn user={user} />}
      showDefault
      nullContent={<EmptyPersonColumn />}
    />
  ),
  groupManager: (
    <ListTableColumn
      label="Group Manager"
      source="groupManager"
      excelSource="groupManager.name"
      column="groupManager"
      settingsGroup="Person"
      formatContent={(user) => <SalesPersonColumn user={user} />}
      showDefault
      nullContent={<EmptyPersonColumn />}
    />
  ),
  telecaller: (
    <ListTableColumn
      label="Telecaller"
      source="telecaller"
      excelSource="telecaller.name"
      column="telecaller"
      settingsGroup="Person"
      formatContent={(user) => <SalesPersonColumn user={user} />}
      showDefault
      nullContent={<EmptyPersonColumn />}
    />
  ),
  supportExecutive: (
    <ListTableColumn
      label="Support Executive"
      source="currentCRMAgent"
      excelSource="currentCRMAgent.name"
      column="currentCRMAgent"
      settingsGroup="Person"
      formatContent={(user) => <CrmAgentColumn user={user} />}
      showDefault
      nullContent={<EmptyPersonColumn />}
    />
  ),
  email: (
    <ListTableColumn
      label="Email"
      source="customer.email"
      column="email"
      settingsGroup="Person"
      sortKey="customer.email"
      sortable
    />
  ),
  createdBy: (
    <ListTableColumn
      label="Created By"
      source="createdBy.name"
      column="createdBy"
      settingsGroup="Person"
    />
  ),
  cityHead: (
    <ListTableColumn
      label="City Head"
      source="leadAssigneeHead"
      excelSource="leadAssigneeHead.name"
      column="leadAssigneeHead"
      settingsGroup="Person"
      formatContent={(user) => <SalesPersonColumn user={user} />}
      nullContent={<EmptyPersonColumn />}
    />
  ),
  supportExecutiveAssignedDate: (
    <ListTableColumn
      label="Support Executive Assigned Date"
      source="crmAssignedAt"
      column="crmAgentAssignedAt"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  transactionManagerAssignedDate: (
    <ListTableColumn
      label="Transaction Manager Assigned Date"
      source="assignedAt"
      column="salesmanAssignedDate"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  cityHeadAssignedDate: (
    <ListTableColumn
      label="City Head Assigned Date"
      source="leadAssigneeHeadAssignedAt"
      column="LeadAssigneeHeadAssignedDate"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  leadManagerAssignedDate: (
    <ListTableColumn
      label="Lead Manager Assigned Date"
      source="leadManagerAssignedAt"
      column="leadManagerAssignedAt"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  groupManagerAssignedDate: (
    <ListTableColumn
      label="Group Manager Assigned Date"
      source="groupManagerAssignedAt"
      column="groupManagerAssignedAt"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  telecallerAssignedDate: (
    <ListTableColumn
      label="Telecaller Assigned Date"
      source="telecallerAssignedAt"
      column="telecallerAssignedAt"
      settingsGroup="Person"
      formatContent={(d: string | Date | moment.Moment) =>
        moment(d).format("DD MMM YYYY hh:mm A")
      }
    />
  ),
  quality: (
    <ListTableColumn
      label="Quality"
      source="quality"
      column="quality"
      settingsGroup="Lead"
      formatContent={(val) => val?.toUpperCase()}
    />
  ),
  transactionManagerAssignedBy: (
    <ListTableColumn
      label="Transaction Manager Assigned By"
      source="salesPersonAssignedBy.name"
      column="salesPersonAssignedBy"
      settingsGroup="Lead"
    />
  ),
  supportExecutiveAssignedBy: (
    <ListTableColumn
      label="Support Executive Assigned By"
      source="executiveAssignedBy.name"
      column="executiveAssignedBy"
      settingsGroup="Lead"
    />
  ),
  leadManagerAssignedBy: (
    <ListTableColumn
      label="Lead Manager Assigned By"
      source="leadManagerAssignedBy.name"
      column="leadManagerAssignedBy"
      settingsGroup="Lead"
    />
  ),
  groupManagerAssignedBy: (
    <ListTableColumn
      label="Group Manager Assigned By"
      source="groupManagerAssignedBy.name"
      column="groupManagerAssignedBy"
      settingsGroup="Lead"
    />
  ),
  telecallerAssignedBy: (
    <ListTableColumn
      label="Telecaller Assigned By"
      source="telecallerAssignedBy.name"
      column="telecallerAssignedBy"
      settingsGroup="Lead"
    />
  ),
};

const createLeadColumnGroup = (columnKeys: LeadColumnKey[]) =>
  columnKeys.map((key) => leadColumns[key]);

const columnsArray : LeadColumnKey[] = [
  "title",
  "date",
  "companyName",
  "contactName",
  "mobile",
  "leadIntent",
  "leadSource",
  "qualityReason",
  "transactionManager",
  "leadManager",
  "groupManager",
  "telecaller",
  "supportExecutive",
  "email",
  "createdBy",
  "cityHead",
  "supportExecutiveAssignedDate",
  "transactionManagerAssignedDate",
  "cityHeadAssignedDate",
  "leadManagerAssignedDate",
  "groupManagerAssignedDate",
  "telecallerAssignedDate",
  "quality",
  "transactionManagerAssignedBy",
  "supportExecutiveAssignedBy",
  "leadManagerAssignedBy",
  "groupManagerAssignedBy",
  "telecallerAssignedBy",
]

export const LeadColumns = createLeadColumnGroup(columnsArray);

export const SelectedLeadColumns = createLeadColumnGroup(columnsArray);
